/** STORY-113 part 3 — the bottom navigation a signed-up student gets, and nobody else.
 *
 * A guest keeps the single-job join screen: `isSignedUp()` is false until a teacher system
 * answered `active`, so a `request` course shows nothing here until the teacher approves.
 *
 * Three destinations, at most. "This lesson" is the live screen unchanged; it points at the
 * sitting this device is in, or back to the join screen when there is none. The other two
 * obey each course's `report_release` policy, which the analytics service already enforces —
 * a course appears once its teacher released it, and a `guest_only` course has no identity to
 * report on at all, so it never appears. No screen behind this bar can show another student.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { isSignedUp } from "../live/studentIdentity";
import { lastJoin } from "../live/studentSession";

/** Renders nothing at all for a guest, so a screen can mount it unconditionally. */
export function BottomNav() {
  const { t } = useTranslation();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (!isSignedUp()) return null;
  const sitting = lastJoin();

  const tab = (active: boolean) =>
    [
      "flex min-h-[var(--control-h)] flex-1 flex-col items-center justify-center gap-0.5 px-2 py-1 text-xs",
      active ? "font-semibold text-[color:var(--accent)]" : "text-[color:var(--ink-2)]",
    ].join(" ");

  const live = path.startsWith("/s/") || path.startsWith("/j");
  return (
    <>
      {/* keeps the last line of a screen clear of the bar */}
      <div aria-hidden className="h-[calc(var(--control-h)+var(--sab)+1.25rem)]" />
      <nav
        aria-label={t("nav.label")}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-[color:var(--line)] bg-[color:var(--surface)] pb-[var(--sab)]"
      >
        <ul className="mx-auto flex max-w-md items-stretch">
          <li className="flex flex-1">
            {sitting ? (
              <Link to="/s/$sessionId" params={{ sessionId: sitting.session_id }} className={tab(live)} aria-current={live ? "page" : undefined}>
                <span aria-hidden className="text-lg leading-none">●</span>
                {t("nav.lesson")}
              </Link>
            ) : (
              <Link to="/j" className={tab(live)} aria-current={live ? "page" : undefined}>
                <span aria-hidden className="text-lg leading-none">●</span>
                {t("nav.lesson")}
              </Link>
            )}
          </li>
          <li className="flex flex-1">
            <Link to="/my/classes" className={tab(path === "/my/classes")} aria-current={path === "/my/classes" ? "page" : undefined}>
              <span aria-hidden className="text-lg leading-none">≡</span>
              {t("nav.classes")}
            </Link>
          </li>
          <li className="flex flex-1">
            <Link to="/my/progress" className={tab(path === "/my/progress")} aria-current={path === "/my/progress" ? "page" : undefined}>
              <span aria-hidden className="text-lg leading-none">◔</span>
              {t("nav.progress")}
            </Link>
          </li>
        </ul>
      </nav>
    </>
  );
}
